import axios from 'axios';
import i18next from 'i18next';
import { Category } from 'src/models';
import { getRootStoreSingleton } from 'src/models/root-store/root-store-singleton';

import { DEFAULT_AXIOS_CONFIG } from './axiosConfig';

// Axios 인스턴스 생성
const axiosInstance = axios.create(DEFAULT_AXIOS_CONFIG);

// 요청 인터셉터 설정
axiosInstance.interceptors.request.use(
  (config) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(true);

    const token = getRootStoreSingleton().authStore.getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] = localStorage.getItem('i18nextLng');
    return config;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);
    return Promise.reject(error);
  },
);

// 응답 인터셉터 설정
axiosInstance.interceptors.response.use(
  async (response) => {
    const data = response.data;
    const config = response.config;

    const authStore = getRootStoreSingleton().authStore;
    const snackbarStore = getRootStoreSingleton().snackbarStore;
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);

    if (data.resultCode === 'F') {
      // 토큰 재발급 실패는 재발급을 부른 쪽에서 세션 만료로 안내한다
      const isRefresh = config.url?.includes('/login/refresh');
      if ((data.errorCode === '401' || data.errorCode === '4001') && !isRefresh) {
        const refreshToken = authStore.getRefreshToken();
        if (refreshToken) {
          const result = await authStore.loginRefresh(refreshToken);
          if (result?.resultCode === 'S') {
            const oauthToken = result.data;
            if (oauthToken) {
              authStore.setAccessToken(oauthToken?.access_token);
              authStore.setRefreshToken(oauthToken?.refresh_token);
            }

            config.headers['Authorization'] = `Bearer ${authStore.getAccessToken()}`;
            return axiosInstance(config);
          }
        }

        authStore.initAuth();
        if (!config.silentAuthError) {
          snackbarStore.setProps({
            open: true,
            category: Category.Error,
            title: i18next.t('common.sessionExpired'),
          });
        }
      } else if (data.errorCode !== '409' && !isRefresh) {
        let errorMessage = '' as string | undefined;
        if (data?.errorMessage) {
          errorMessage = data?.errorMessage;
        } else if (data?.err_msg) {
          errorMessage = data?.err_msg;
        }

        snackbarStore.setProps({
          open: true,
          category: Category.Error,
          title: errorMessage ? errorMessage : i18next.t('common.commonError'),
        });
      }
    }

    return response;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);

    const snackbarStore = getRootStoreSingleton().snackbarStore;
    if (error.code === 'ERR_NETWORK') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.serverError'),
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
